import { SpanStatusCode, trace } from '@cms/telemetry-api';

declare global {
    interface RequestInit {
        url?: string; // the URL template
    }
}

/**
 * Adds tracing to api client fetches so that we can trace cached fetch requests (next.js) and add the URL template.
 */
export const getFetchWithTrace = (fetchFn: typeof fetch): typeof fetch => {
    return (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
        const url = input instanceof Request ? input.url : input.toString();
        const headers = input instanceof Request ? input.headers : init?.headers;
        const method = init?.method || 'GET';

        let spanName = `api-client - ${method}`;

        if (headers instanceof Headers) {
            const template = headers.get('x-otel-url-template');

            if (template) {
                spanName += ` ${template}`;
            }
        }

        const tracer = trace.getTracer('api-client');

        return tracer.startActiveSpan(spanName, async (span) => {
            try {
                span.setAttribute('api-client.http.url', url);
                return await fetchFn(input, init);
            } catch (err) {
                span.recordException(err as Error);
                span.setStatus({
                    code: SpanStatusCode.ERROR,
                    message: err instanceof Error ? err.message : String(err),
                });
                throw err;
            } finally {
                span.end();
            }
        });
    };
};
